import { configManager } from "@/features/client-config/services/config-manager";
import { onCourseTracerStore } from "@/features/trajectory/services/on-course-tracer-store";
import { useMountEffect } from "@/hooks/use-mount-effect";
import { sessionTracking } from "@/services/analytics/session-tracking";
import { cacheManager } from "@/services/pwa/cache-manager/cache-manager";
import { storageManager } from "@/services/storage/storage/storage";
import { generateUUID } from "@/utils/uuid";
import { useState, useRef } from "react";

import { globalWebSocketManager } from "../../services/global-websocket-manager";
import type { Shot } from "../../types/shot.types";
import { upsertShot } from "../../utils/upsert-shot";
import {
  SessionShotsContext,
  type SessionShotsContextValue,
} from "./session-shots-context-definition";

/**
 * Provider for session shots management
 * Simple React context pattern - shots are managed in state (in-memory only)
 *
 * TODO: Add persistence layer for shots and session data (e.g., IndexedDB)
 * Currently, shots are lost on page reload
 */
export const SessionShotsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  "use no memo";
  const [shots, setShotsState] = useState<Shot[]>([]);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [consented, setConsented] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Refs for latest values — read by WebSocket callbacks without re-registering
  const shotsRef = useRef<Shot[]>([]);
  const consentedRef = useRef<boolean>(false);
  const sessionIdRef = useRef<string | null>(null);

  // Keep refs in sync with state (assign in render body, safe in React 19)
  shotsRef.current = shots;
  consentedRef.current = consented;

  const MAX_SHOTS_PER_SESSION = 50;

  const addShot = (shot: Shot) => {
    setShotsState((prev) => upsertShot(prev, shot, MAX_SHOTS_PER_SESSION));
  };

  const updateShot = (updatedShot: Shot) => {
    setShotsState((prev) =>
      prev.map((s) => (s.id === updatedShot.id ? updatedShot : s))
    );
  };

  const removeShot = (shotId: string) => {
    setShotsState((prev) => prev.filter((s) => s.id !== shotId));
  };

  const getShots = () => shotsRef.current;

  const getConsented = () => consentedRef.current;

  const resetSessionState = () => {
    void cacheManager.clearCurrentSession().catch(() => {});
    onCourseTracerStore.clear();
    storageManager.revokeAllSnapshotUrls();
    void storageManager.clearAllSnapshots().catch(() => {});
    setShotsState([]);
    setStartTime(null);
    sessionIdRef.current = generateUUID();
    sessionTracking.setCurrentSessionId(sessionIdRef.current);
    setStartTime(Date.now());
  };

  // reason distinguishes an explicit end-session confirm from an inactivity
  // timeout auto-ending the session — see SessionModalsContext callers.
  const endSession = (reason: "manual" | "timeout") => {
    sessionTracking.end({
      shotCount: shotsRef.current.length,
      endedBy: reason,
    });
    resetSessionState();
    setConsented(false);
  };

  // Used whenever the home page is (re)visited. Consent is cleared here too
  // so returning to Home (e.g. via back navigation, skipping endSession)
  // can't leave a stale consent from a prior session in place. Not an
  // analytics session start/end — no session was showing.
  const startNewSession = () => {
    resetSessionState();
    setConsented(false);
  };

  // Explicit user action (tapping "Begin") is what consents the first shot,
  // and is this app's actual session start — not to be confused with
  // PostHog's own autocapture-based session, which is disabled. Doesn't
  // rotate the session id: it must stay the one assigned by
  // startNewSession/mount so pre-Begin events (terms_opened,
  // begin_button_tapped) correlate with the session_started they precede.
  const beginSession = () => {
    setConsented(true);
    sessionTracking.start({ startedBy: "manual" });
  };

  useMountEffect(() => {
    const initializeSession = async () => {
      try {
        setIsLoading(true);

        sessionIdRef.current = generateUUID();
        sessionTracking.setCurrentSessionId(sessionIdRef.current);
        setShotsState([]);
        setStartTime(Date.now());

        await configManager.getConfig();
        setIsInitialized(true);
      } catch (error) {
        console.error("Failed to initialize session:", error);
        sessionIdRef.current = generateUUID();
        sessionTracking.setCurrentSessionId(sessionIdRef.current);
        setStartTime(Date.now());
        setIsInitialized(true);
      } finally {
        setIsLoading(false);
      }
    };

    initializeSession();
  });

  useMountEffect(() => {
    globalWebSocketManager.registerContext({
      addShot,
      updateShot,
      getShots,
      consented: getConsented,
      setConsented,
    });

    return () => {
      globalWebSocketManager.registerContext(null);
    };
  });

  if (!isInitialized || sessionIdRef.current === null) {
    return null;
  }

  const value: SessionShotsContextValue = {
    shots,
    sessionId: sessionIdRef.current,
    startTime,
    isLoading,
    consented,
    endSession,
    startNewSession,
    beginSession,
    setConsented,
    updateShot,
    removeShot,
  };

  return (
    <SessionShotsContext.Provider value={value}>
      {children}
    </SessionShotsContext.Provider>
  );
};
